import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  ViewChild,
  forwardRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ZagIcon } from '../icon/icon';

/**
 * TagsInput Component
 *
 * A ControlValueAccessor input that lets users enter individual tags (strings)
 * by pressing Enter, comma, or Space. Each tag is rendered as a dismissible pill.
 * An optional `validator` function drives the pill's visual state —
 * valid tags use the primary colour, invalid ones use the error colour.
 *
 * The CVA value is `string[]` — one entry per committed tag.
 *
 * Keyboard interaction:
 * - **Enter / , / Space** → commit current text as a tag and clear the field
 * - **Backspace** on empty input → remove the last tag
 * - Clicking the **×** button on a pill removes that tag
 * - Blurring the field with pending text commits it as a tag
 *
 * @example Email invite use-case
 * ```html
 * <zag-tags-input
 *   formControlName="emails"
 *   placeholder="user@example.com"
 *   [validator]="isValidEmail"
 * />
 * ```
 *
 * @example Generic tags (no validation)
 * ```html
 * <zag-tags-input formControlName="tags" placeholder="Add a tag..." />
 * ```
 */
@Component({
  selector: 'zag-tags-input',
  imports: [ZagIcon],
  templateUrl: './tags-input.html',
  styleUrl: './tags-input.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TagsInput),
      multi: true,
    },
  ],
  host: {
    class: 'zag-tags-input-host',
  },
})
export class TagsInput implements ControlValueAccessor {
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild('inputEl') private readonly inputEl?: ElementRef<HTMLInputElement>;

  /** Placeholder shown when no tags have been committed yet */
  readonly placeholder = input<string>('');

  /** Disables the entire component */
  readonly disabled = input<boolean>(false);

  /** Forwarded to the inner `<input>` element for label association */
  readonly id = input<string | undefined>(undefined);

  /** aria-describedby forwarded to the inner `<input>` element */
  readonly ariaDescribedby = input<string | undefined>(undefined);

  /**
   * Optional predicate for per-pill visual validity feedback.
   * When `null` (default) every pill uses the primary (valid) style.
   */
  readonly validator = input<((value: string) => boolean) | null>(null);

  // ── Internal state ──────────────────────────────────────────────────────────

  private readonly _tags = signal<string[]>([]);
  protected readonly tags = this._tags.asReadonly();

  protected readonly focused = signal(false);

  // ── CVA callbacks ────────────────────────────────────────────────────────────

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: string[]) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  writeValue(value: string[] | null): void {
    this._tags.set(value ?? []);
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(_isDisabled: boolean): void {
    this.cdr.markForCheck();
  }

  // ── Template helpers ─────────────────────────────────────────────────────────

  protected isTagValid(tag: string): boolean {
    const fn = this.validator();
    return fn === null || fn(tag);
  }

  protected onKeydown(event: KeyboardEvent): void {
    const inputValue = (event.target as HTMLInputElement).value;

    if (event.key === 'Enter' || event.key === ',' || event.key === ' ') {
      event.preventDefault();
      this.commitInput(inputValue);
      return;
    }

    if (event.key === 'Backspace' && inputValue === '') {
      this.removeLastTag();
    }
  }

  protected onBlur(): void {
    this.focused.set(false);
    const pending = this.inputEl?.nativeElement.value ?? '';
    if (pending.trim()) {
      this.commitInput(pending);
    }
    this.onTouched();
  }

  protected onFocus(): void {
    this.focused.set(true);
  }

  protected onPillRemoveClick(index: number, event: MouseEvent): void {
    event.stopPropagation();
    this.removeTag(index);
  }

  protected removeTag(index: number): void {
    const updated = [...this._tags()];
    updated.splice(index, 1);
    this._tags.set(updated);
    this.onChange(updated);
    this.cdr.markForCheck();
  }

  private commitInput(raw: string): void {
    const trimmed = raw.trim().replace(/,$/, '').trim();
    if (!trimmed) return;

    const updated = [...this._tags(), trimmed];
    this._tags.set(updated);
    this.onChange(updated);

    if (this.inputEl?.nativeElement) {
      this.inputEl.nativeElement.value = '';
    }
    this.cdr.markForCheck();
  }

  private removeLastTag(): void {
    const current = this._tags();
    if (current.length === 0) return;
    const updated = current.slice(0, -1);
    this._tags.set(updated);
    this.onChange(updated);
    this.cdr.markForCheck();
  }
}
